import { useState, type ComponentPropsWithoutRef } from 'react'
import { useController, useForm, type Control, type FieldErrors } from 'react-hook-form'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'

import { Alert, Button, Dialog } from '@atoms'
import { RadioGroup } from '@components/atoms/RadioGroup'
import Checkbox from '@components/atoms/form/Checkbox'
import { SelectField } from '@components/molecules/SelectField'
import { EventRsvpPicker, type EventRsvp } from '@components/molecules/EventRsvpPicker'
import cn from '@components/utils/cn'
import Icon, { type IconTypes } from '@components/utils/Icon'

import {
  CompleteCallbackField,
  ScheduleCallbackFields,
  callbackDraftError,
  callbackDueAt,
  callbackOwner,
  existingCallbackNotice,
  inputClass,
  labelClass,
  newCallbackDraft,
  type AssignConfig,
  type CallbackDraft,
} from './ScheduleCallbackFields'

export type Outcome =
  | 'meaningful_interaction'
  | 'callback_requested'
  | 'left_message'
  | 'no_answer'
  | 'not_interested'
  | 'inaccessible'
  | 'other'

export const CALL_OUTCOMES: Array<{ value: Outcome; label: string; icon: IconTypes }> = [
  { value: 'meaningful_interaction', label: 'Meaningful interaction', icon: 'message-chat-square' },
  { value: 'callback_requested', label: 'Callback requested', icon: 'calendar-check-01' },
  { value: 'left_message', label: 'Left message', icon: 'message-circle' },
  { value: 'no_answer', label: 'No answer', icon: 'phone-off' },
  { value: 'not_interested', label: 'Not interested', icon: 'x-circle' },
  { value: 'inaccessible', label: 'Inaccessible', icon: 'alert-triangle' },
  { value: 'other', label: 'Other', icon: 'help-circle' },
]
export const OUTCOME_LABELS = Object.fromEntries(CALL_OUTCOMES.map((o) => [o.value, o.label])) as Record<
  Outcome,
  string
>

// The server sends these in #7415 (SUCCESSFUL_CONTACT_STATUSES): picking one
// pre-ticks "No further contact attempts needed".
export const SUCCESSFUL_CONTACT_STATUSES: Outcome[] = ['meaningful_interaction', 'not_interested', 'inaccessible']

// The real form's Contact Outcome: whether the call did what the session or list
// set out to do. Asked once no further attempts are needed, as LogContactDialog does.
export type ContactOutcome = 'successful' | 'unsuccessful'

const CONTACT_OUTCOMES: Array<{ value: ContactOutcome; label: string }> = [
  { value: 'successful', label: 'Successful' },
  { value: 'unsuccessful', label: 'Unsuccessful' },
]

export function ContactOutcomeField({ control, errors }: { control: Control<any>; errors: FieldErrors }) {
  const { field } = useController({
    name: 'contactOutcome',
    control,
    rules: { required: 'Pick a contact outcome.' },
  })
  return (
    <SelectField
      name={field.name}
      ref={field.ref}
      value={field.value}
      onValueChange={field.onChange}
      label="Contact outcome"
      placeholder="Select outcome"
      options={CONTACT_OUTCOMES}
      errors={errors}
      required
    />
  )
}

// #7415's RadioGroupCard atom (app/frontend/components/atoms/RadioGroup.tsx on
// that branch). Swap for the real import once #7415 is merged.
export function RadioGroupCard({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>) {
  return (
    <RadioGroupPrimitive.Item
      className={cn(
        'tw:relative tw:flex tw:min-h-[92px] tw:flex-col tw:items-center tw:justify-center tw:gap-2 tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:p-4 tw:text-center tw:text-gray-900 tw:hover:border-primary-400 tw:hover:text-primary-700 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-300 tw:data-[state=checked]:border-2 tw:data-[state=checked]:border-primary-500 tw:data-[state=checked]:bg-primary-50 tw:data-[state=checked]:text-primary-700 tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
        className
      )}
      {...props}
    >
      {children}
      <RadioGroupPrimitive.Indicator className="tw:absolute tw:right-2 tw:top-2 tw:text-primary-600">
        <Icon type="check-circle" className="tw:h-4 tw:w-4" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
}

const daysFromNow = (days: number, hour: number) => {
  const d = new Date()
  d.setDate(d.getDate() + days)
  d.setHours(hour, 0, 0, 0)
  return d.toISOString()
}

// Upcoming events for the RSVP picker, as the real log contact form lists them.
export const SAMPLE_EVENTS = [
  {
    id: 'e1',
    name: 'Saturday doorknock',
    startTime: daysFromNow(4, 10),
    ianaTimeZone: 'Europe/Rome',
    isFull: false,
    hasRsvpFromSupporter: true,
    isPast: false,
    liveShifts: [],
  },
  {
    id: 'e2',
    name: 'Phone bank',
    startTime: daysFromNow(6, 18),
    ianaTimeZone: 'Europe/Rome',
    isFull: true,
    hasRsvpFromSupporter: false,
    isPast: false,
    liveShifts: [],
  },
  {
    id: 'e3',
    name: 'Market stall',
    startTime: daysFromNow(9, 8),
    ianaTimeZone: 'Europe/Rome',
    isFull: false,
    hasRsvpFromSupporter: false,
    isPast: false,
    liveShifts: [],
  },
]

export type LoggedContact = {
  outcome: Outcome
  contactOutcome: ContactOutcome | null
  note: string
  callback: CallbackDraft | null
  completeCallback: boolean
}

function rescheduleDraft(open: CallbackDraft, meId: string, now: Date): CallbackDraft {
  const assigneeId = callbackOwner(open, meId) === 'other' ? meId : open.assigneeId
  if (callbackDueAt(open) > now) return { ...open, assigneeId }
  return { ...newCallbackDraft(now, assigneeId, open.notes), flexible: open.flexible }
}

export function LogContactForm({
  supporter,
  openCallback,
  ownerName,
  meId,
  now,
  assign,
  onCall = false,
  inSession = false,
  onSave,
}: {
  supporter: { fullName: string; firstName: string }
  openCallback: CallbackDraft | null
  ownerName: string
  meId: string
  now: Date
  assign: AssignConfig
  onCall?: boolean
  // Logged from a calling session: asks whether more attempts are needed, then the contact outcome.
  inSession?: boolean
  onSave: (contact: LoggedContact) => void
}) {
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [callback, setCallback] = useState<CallbackDraft>(() =>
    openCallback ? rescheduleDraft(openCallback, meId, now) : newCallbackDraft(now, meId)
  )
  const {
    control,
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      completeCallback: false,
      callCompleted: false,
      contactOutcome: null as ContactOutcome | null,
      note: '',
      eventRsvps: [] as EventRsvp[],
    },
  })
  const { field: eventRsvpsField } = useController({ name: 'eventRsvps', control })
  const callbackRequested = outcome === 'callback_requested'
  // A callback is a further contact attempt, so neither applies to one.
  const asksOutcome = inSession && !callbackRequested && watch('callCompleted')
  const canSave =
    !!outcome && !(callbackRequested && callbackDraftError(callback, now)) && !(asksOutcome && !watch('contactOutcome'))

  const save = () => {
    if (!outcome) return
    const { completeCallback, contactOutcome, note } = getValues()
    onSave({
      outcome,
      contactOutcome: asksOutcome ? contactOutcome : null,
      note,
      callback: callbackRequested ? callback : null,
      completeCallback: !!openCallback && !callbackRequested && completeCallback,
    })
  }

  return (
    <>
      <Dialog.Header>
        <Dialog.Title>Log contact with {supporter.fullName}</Dialog.Title>
      </Dialog.Header>

      {onCall && <Alert type="success" size="sm" title={`You are now calling ${supporter.fullName}`} />}

      <RadioGroup
        aria-label="How did the call go?"
        value={outcome ?? ''}
        onValueChange={(value) => {
          setOutcome(value as Outcome)
          setValue('completeCallback', SUCCESSFUL_CONTACT_STATUSES.includes(value as Outcome))
          setValue('callCompleted', SUCCESSFUL_CONTACT_STATUSES.includes(value as Outcome))
        }}
        className="tw:grid-cols-2 tw:gap-2"
      >
        {CALL_OUTCOMES.map(({ value, label, icon }) => (
          <RadioGroupCard
            key={value}
            value={value}
            className="tw:min-h-0 tw:flex-row tw:justify-start tw:gap-2.5 tw:py-2.5 tw:pl-3 tw:pr-8 tw:text-left"
          >
            <Icon type={icon} className="tw:h-5 tw:w-5 tw:shrink-0" />
            <span className="tw:text-sm tw:font-medium">{label}</span>
          </RadioGroupCard>
        ))}
      </RadioGroup>

      {callbackRequested && (
        <section className="tw:rounded-lg tw:border tw:border-primary-200 tw:bg-primary-50/40 tw:p-4">
          <h3 className="tw:text-base tw:font-semibold tw:text-gray-900">
            {openCallback ? 'Reschedule the callback' : 'Schedule the callback'}
          </h3>
          <p className="tw:mb-4 tw:mt-1 tw:text-sm tw:text-gray-600">
            {openCallback
              ? existingCallbackNotice(supporter.firstName, openCallback, ownerName, meId, now)
              : `${supporter.firstName} drops off the call list until then. When it's due, they come back to the top.`}
          </p>
          <ScheduleCallbackFields draft={callback} onChange={setCallback} now={now} assign={assign} />
        </section>
      )}

      {openCallback && !callbackRequested && (
        <CompleteCallbackField control={control} callback={openCallback} assigneeName={ownerName} now={now} />
      )}

      {inSession && !callbackRequested && (
        <div className="tw:flex tw:flex-wrap tw:items-center">
          <Checkbox
            name="callCompleted"
            control={control}
            errors={errors}
            description="When checked, this person will be removed from future calling attempts in this session or list"
          >
            No further contact attempts needed
          </Checkbox>
        </div>
      )}

      {asksOutcome && <ContactOutcomeField control={control} errors={errors} />}

      <EventRsvpPicker events={SAMPLE_EVENTS} value={eventRsvpsField.value} onChange={eventRsvpsField.onChange} />

      <label className="tw:flex tw:flex-col tw:gap-1">
        <span className={labelClass}>Note</span>
        <textarea className={inputClass} rows={3} {...register('note')} />
      </label>

      <Dialog.Footer className="tw:mt-2 tw:gap-2">
        <Dialog.Close asChild>
          <Button rank="secondary">Cancel</Button>
        </Dialog.Close>
        <Button disabled={!canSave} onClick={save}>
          {callbackRequested
            ? openCallback
              ? 'Save and move callback'
              : 'Save and schedule callback'
            : 'Save contact'}
        </Button>
      </Dialog.Footer>
    </>
  )
}
